/**
 * Prebuilt Report Builder configurations reachable via /reports/builder?preset=<key>.
 * Filters with empty values are optional placeholders the user can fill in.
 */

import { formatDateInputValue } from './dateUtils'
import type { FilterRow } from './reportFilters'

export interface ReportPreset {
  key: string
  name: string
  description: string
  dataSource: string
  columns: string[]
  filters: FilterRow[]
  groupBy: string
}

export function buildReportPreset(
  key: string | null | undefined,
  now: Date = new Date()
): ReportPreset | null {
  if (!key) return null
  const today = formatDateInputValue(now)
  const monthStart = formatDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1))

  if (key === 'dsny-after-6pm') {
    return {
      key,
      name: 'Garbage (DSNY) Jobs Completed After 6 PM',
      description:
        'Completed garbage/DSNY jobs finished after 6:00 PM. Adjust the completion dates, or type a name into the Employee filter to narrow to one worker. Group by Employee for per-worker counts.',
      dataSource: 'jobs',
      columns: [
        'job_number',
        'title',
        'property.address',
        'employee.name',
        'crew.name',
        'status',
        'actual_end_time',
      ],
      filters: [
        { column: 'is_garbage_job', operator: 'is_true', value: '' },
        { column: 'status', operator: 'equals', value: 'completed' },
        { column: 'actual_end_time', operator: 'time_gte', value: '18:00' },
        { column: 'actual_end_time', operator: 'gte', value: monthStart },
        { column: 'actual_end_time', operator: 'lte', value: today },
        { column: 'employee.name', operator: 'contains', value: '' },
      ],
      groupBy: '',
    }
  }

  if (key === 'incomplete-jobs') {
    return {
      key,
      name: 'Incomplete Jobs',
      description:
        'Jobs in any status other than completed or cancelled, with worker and property address. Adjust the scheduled dates, or fill the Employee / Property filters to narrow results.',
      dataSource: 'jobs',
      columns: [
        'job_number',
        'title',
        'status',
        'scheduled_date',
        'property.address',
        'employee.name',
        'crew.name',
      ],
      filters: [
        { column: 'status', operator: 'not_in_list', value: 'completed,cancelled' },
        { column: 'scheduled_date', operator: 'gte', value: monthStart },
        { column: 'scheduled_date', operator: 'lte', value: today },
        { column: 'employee.name', operator: 'contains', value: '' },
        { column: 'property.address', operator: 'contains', value: '' },
      ],
      groupBy: '',
    }
  }

  return null
}
